import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Trash2, Upload, LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "tt_admin_pw";
const TIERS = ["Founding Partner", "Community Partner", "Donation-Bin Partner", "Corporate Sponsor", "In-Kind Partner"];

export default function Admin() {
  const [pw, setPw] = useState(localStorage.getItem(PW_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [tab, setTab] = useState("sponsors");

  const authHeader = { headers: { "X-Admin-Password": pw } };

  const verify = useCallback(async (password) => {
    try {
      await axios.post(`${API}/admin/verify`, { password });
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  useEffect(() => {
    if (pw) verify(pw).then((ok) => setAuthed(ok));
  }, [pw, verify]);

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    const ok = await verify(pwInput);
    if (ok) {
      localStorage.setItem(PW_KEY, pwInput);
      setPw(pwInput);
      setAuthed(true);
    } else {
      setLoginErr("Incorrect password.");
    }
  };

  const logout = () => {
    localStorage.removeItem(PW_KEY);
    setPw("");
    setAuthed(false);
    setPwInput("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/15 p-8 bg-white/[0.02]">
          <Lock size={22} className="text-white" />
          <h1 className="font-serif text-3xl font-light text-white mt-5">Foundation Admin</h1>
          <p className="text-white/50 text-sm mt-2">Enter the shared password to continue.</p>
          <input
            type="password"
            data-testid="admin-password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-4 py-3 text-white mt-6"
            placeholder="Password"
          />
          {loginErr && <p className="text-sm text-white/80 mt-3" data-testid="admin-login-error">{loginErr}</p>}
          <button type="submit" data-testid="admin-login-submit" className="w-full overline mt-6 px-5 py-4 bg-white text-black hover:bg-white/85 transition-colors">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-10 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-4xl font-light text-white">Admin</h1>
        <button onClick={logout} className="inline-flex items-center gap-2 overline text-white/60 hover:text-white"><LogOut size={14} /> Log out</button>
      </div>

      <div className="flex gap-2 mt-8 border-b border-white/10">
        {[["sponsors", "Sponsors"], ["partners", "Partners"], ["pledges", "Pledges"], ["updates", "Updates"], ["documents", "Documents"], ["donations", "Donations"], ["subscribers", "Subscribers"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`overline px-5 py-3 border-b-2 -mb-px transition-colors ${tab === id ? "border-white text-white" : "border-transparent text-white/50 hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "sponsors" && <SponsorsTab authHeader={authHeader} />}
        {tab === "partners" && <PartnersTab authHeader={authHeader} />}
        {tab === "pledges" && <PledgesTab authHeader={authHeader} />}
        {tab === "updates" && <UpdatesTab authHeader={authHeader} />}
        {tab === "documents" && <DocumentsTab authHeader={authHeader} />}
        {tab === "donations" && <DonationsTab authHeader={authHeader} />}
        {tab === "subscribers" && <SubscribersTab authHeader={authHeader} />}
      </div>
    </div>
  );
}

function SponsorsTab({ authHeader }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", website: "", tier: TIERS[1], logo: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/sponsors`);
    setList(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { setErr("Logo must be under 1.5MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name) { setErr("Name is required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/sponsors`, form, authHeader);
      setForm({ name: "", location: "", website: "", tier: TIERS[1], logo: "" });
      await load();
    } catch (e2) {
      setErr("Could not save sponsor. Check your session.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/sponsors/${id}`, authHeader);
    await load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={add} data-testid="sponsor-form" className="lg:col-span-5 border border-white/15 p-6 bg-white/[0.02] h-fit">
        <p className="overline text-white/50">Add a partner</p>
        <div className="space-y-4 mt-5">
          <input className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white" placeholder="Name *" data-testid="sponsor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white" placeholder="Location (e.g. Apollo Beach, FL)" data-testid="sponsor-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <input className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white" placeholder="Website (https://…)" data-testid="sponsor-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          <select className="w-full bg-black border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white" data-testid="sponsor-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-3 border border-dashed border-white/25 px-3 py-3 text-white/60 cursor-pointer hover:border-white/50">
            <Upload size={16} /> <span className="text-sm">{form.logo ? "Logo selected — change" : "Upload logo (PNG/JPG)"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="sponsor-logo" onChange={onLogo} />
          </label>
          {form.logo && <img src={form.logo} alt="preview" className="h-16 w-auto object-contain bg-white/5 p-2" />}
        </div>
        {err && <p className="text-sm text-white/80 mt-4">{err}</p>}
        <button type="submit" disabled={saving} data-testid="sponsor-submit" className="w-full overline mt-6 px-5 py-3 bg-white text-black hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? "Saving…" : "Add Partner"}</button>
      </form>

      <div className="lg:col-span-7">
        <div className="flex items-center justify-between">
          <p className="overline text-white/50">Current partners ({list.length})</p>
          <button onClick={load} className="inline-flex items-center gap-2 overline text-white/50 hover:text-white"><RefreshCw size={13} /> Refresh</button>
        </div>
        <div className="mt-5 space-y-3" data-testid="sponsor-list">
          {list.length === 0 && <p className="text-white/40 font-light">No partners yet. Add your first above.</p>}
          {list.map((s) => (
            <div key={s.id} className="flex items-center gap-4 border border-white/12 p-4">
              {s.logo ? <img src={s.logo} alt={s.name} className="h-12 w-12 object-contain bg-white/5 p-1" /> : <div className="h-12 w-12 bg-white/5 flex items-center justify-center text-white/40 font-serif">{s.name?.[0]}</div>}
              <div className="flex-1 min-w-0">
                <p className="text-white font-light truncate">{s.name}</p>
                <p className="text-white/45 text-xs">{s.tier}{s.location ? ` · ${s.location}` : ""}</p>
              </div>
              <button onClick={() => remove(s.id)} data-testid={`sponsor-delete-${s.id}`} className="text-white/40 hover:text-white p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UpdatesTab({ authHeader }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", image: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/posts`);
    setList(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { setErr("Image must be under 3MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title || !form.body) { setErr("Title and message are required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/posts`, form, authHeader);
      setForm({ title: "", body: "", image: "" });
      await load();
    } catch (e2) {
      setErr("Could not save update. Check your session.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/posts/${id}`, authHeader);
    await load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={add} data-testid="post-form" className="lg:col-span-5 border border-white/15 p-6 bg-white/[0.02] h-fit">
        <p className="overline text-white/50">Post an update</p>
        <div className="space-y-4 mt-5">
          <input className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white" placeholder="Title *" data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea rows={6} className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-2.5 text-white resize-y" placeholder="Share what happened — a drive, a milestone, a thank-you… *" data-testid="post-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <label className="flex items-center gap-3 border border-dashed border-white/25 px-3 py-3 text-white/60 cursor-pointer hover:border-white/50">
            <Upload size={16} /> <span className="text-sm">{form.image ? "Photo selected \u2014 change" : "Add a photo (optional)"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="post-image" onChange={onImage} />
          </label>
          {form.image && <img src={form.image} alt="preview" className="w-full max-h-48 object-cover" />}
        </div>
        {err && <p className="text-sm text-white/80 mt-4">{err}</p>}
        <button type="submit" disabled={saving} data-testid="post-submit" className="w-full overline mt-6 px-5 py-3 bg-white text-black hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? "Publishing\u2026" : "Publish Update"}</button>
      </form>

      <div className="lg:col-span-7">
        <div className="flex items-center justify-between">
          <p className="overline text-white/50">Published updates ({list.length})</p>
          <button onClick={load} className="inline-flex items-center gap-2 overline text-white/50 hover:text-white"><RefreshCw size={13} /> Refresh</button>
        </div>
        <div className="mt-5 space-y-3" data-testid="post-list">
          {list.length === 0 && <p className="text-white/40 font-light">No updates yet. Share your first above.</p>}
          {list.map((p) => (
            <div key={p.id} className="flex items-start gap-4 border border-white/12 p-4">
              {p.image && <img src={p.image} alt={p.title} className="h-16 w-16 object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white font-light">{p.title}</p>
                <p className="text-white/50 text-xs mt-1 line-clamp-2">{p.body}</p>
              </div>
              <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.id}`} className="text-white/40 hover:text-white p-2"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ authHeader }) {
  const [meta, setMeta] = useState([]);
  const [filename, setFilename] = useState("");
  const [data, setData] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/documents/meta`);
    setMeta(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = meta.find((m) => m.key === "determination-letter");

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    if (file.type !== "application/pdf") { setErr("Please upload a PDF file."); return; }
    if (file.size > 8 * 1024 * 1024) { setErr("File must be under 8MB."); return; }
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => setData(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setErr("");
    if (!data) { setErr("Choose a PDF first."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/documents`, { key: "determination-letter", filename: filename || "determination-letter.pdf", data }, authHeader);
      setData(""); setFilename("");
      await load();
    } catch (e2) {
      setErr("Upload failed. Check your session and try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await axios.delete(`${API}/documents/determination-letter`, authHeader);
    await load();
  };

  return (
    <div className="max-w-2xl" data-testid="documents-tab">
      <p className="overline text-white/50">IRS 501(c)(3) Determination Letter</p>
      <p className="text-white/55 font-light text-sm mt-3 leading-relaxed">
        Upload your IRS determination letter (PDF). Once uploaded, a "501(c)(3) Determination Letter" download
        button appears for funders on the Our Work page and in the footer.
      </p>

      {current && (
        <div className="mt-6 flex items-center gap-4 border border-white/15 p-4" data-testid="current-letter">
          <span className="text-white/85 flex-1 truncate">{current.filename || "determination-letter.pdf"}</span>
          <a href={`${API}/documents/determination-letter`} target="_blank" rel="noopener noreferrer" className="overline text-white/60 hover:text-white">View</a>
          <button onClick={remove} className="text-white/40 hover:text-white p-1"><Trash2 size={16} /></button>
        </div>
      )}

      <label className="mt-6 flex items-center gap-3 border border-dashed border-white/25 px-4 py-4 text-white/60 cursor-pointer hover:border-white/50">
        <Upload size={16} /> <span className="text-sm">{filename ? `Selected: ${filename}` : (current ? "Replace letter (PDF)" : "Choose letter PDF")}</span>
        <input type="file" accept="application/pdf" className="hidden" data-testid="letter-file" onChange={onFile} />
      </label>
      {err && <p className="text-sm text-white/80 mt-3">{err}</p>}
      <button onClick={save} disabled={saving || !data} data-testid="letter-upload" className="overline mt-5 px-6 py-3 bg-white text-black hover:bg-white/85 transition-colors disabled:opacity-50">
        {saving ? "Uploading…" : "Upload Letter"}
      </button>
    </div>
  );
}

function DonationsTab({ authHeader }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/donations`, authHeader);
    setData(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-white/40">Loading…</p>;
  const s = data.summary || {};
  const cards = [
    ["Total Raised", `$${(s.total_raised || 0).toLocaleString()}`],
    ["Completed Gifts", s.paid_count || 0],
    ["Monthly Donors", s.monthly_count || 0],
    ["Monthly Value", `$${(s.monthly_recurring_value || 0).toLocaleString()}`],
  ];
  return (
    <div data-testid="donations-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
        {cards.map(([label, val]) => (
          <div key={label} className="bg-black p-6">
            <p className="overline text-white/45">{label}</p>
            <p className="font-serif text-3xl md:text-4xl font-light text-white mt-3">{val}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/45 overline border-b border-white/10">
              <th className="py-3 pr-4">Date</th><th className="py-3 pr-4">Donor</th><th className="py-3 pr-4">Amount</th><th className="py-3 pr-4">Type</th><th className="py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.donations || []).map((d) => (
              <tr key={d.id || d.session_id} className="border-b border-white/5 text-white/80">
                <td className="py-3 pr-4 whitespace-nowrap">{(d.created_at || "").slice(0, 10)}</td>
                <td className="py-3 pr-4">{d.donor_name || d.donor_email || "Anonymous"}</td>
                <td className="py-3 pr-4">${d.amount}</td>
                <td className="py-3 pr-4 capitalize">{d.frequency}</td>
                <td className="py-3"><span className={d.payment_status === "paid" ? "text-white" : "text-white/45"}>{d.payment_status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {(data.donations || []).length === 0 && <p className="text-white/40 mt-4">No donations recorded yet.</p>}
      </div>
    </div>
  );
}

function PledgesTab({ authHeader }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/pledges`, authHeader);
    setData(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const cols = ["created_at", "program", "experience", "amount", "name", "email", "phone", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat((data.pledges || []).map((p) => cols.map((c) => esc(p[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tandem-t-pledges-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <p className="text-white/40">Loading…</p>;
  return (
    <div data-testid="pledges-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <p className="overline text-white/50">Next Move Experience Pledges ({data.count})</p>
        <button
          onClick={exportCsv}
          disabled={data.count === 0}
          data-testid="pledges-export-csv"
          className="overline px-5 py-3 border border-white/25 text-white hover:bg-white hover:text-black transition-colors disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>
      {data.count === 0 && <p className="text-white/40 mt-6">No experience pledges yet.</p>}
      <div className="mt-6 space-y-4">
        {(data.pledges || []).map((p) => (
          <div key={p.id} data-testid="pledge-row" className="border border-white/12 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-serif text-white text-xl font-light">{p.experience}</p>
                <p className="text-white/60 text-sm mt-1">{p.name}</p>
              </div>
              <span className="text-white/40 text-xs">{(p.created_at || "").slice(0, 10)}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.program && <span className="overline text-[10px] px-3 py-1 border border-white/30 text-white/85">{p.program}</span>}
              {p.amount && <span className="overline text-[10px] px-3 py-1 border border-white/20 text-white/70">{p.amount}</span>}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-white/75">
              <p><a href={`mailto:${p.email}`} className="hover:text-white underline">{p.email}</a></p>
              {p.phone && <p><a href={`tel:${p.phone}`} className="hover:text-white">{p.phone}</a></p>}
            </div>
            {p.message && <p className="text-white/60 text-sm mt-3 italic">&ldquo;{p.message}&rdquo;</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnersTab({ authHeader }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/partners`, authHeader);
    setData(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const cols = ["created_at", "business", "contact_name", "email", "phone", "recognition_name", "commitment", "amount", "contribution_type", "in_kind_description", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat((data.partners || []).map((p) => cols.map((c) => esc(p[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tandem-t-partners-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <p className="text-white/40">Loading…</p>;
  return (
    <div data-testid="partners-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <p className="overline text-white/50">Community Partner Inquiries ({data.count})</p>
        <button
          onClick={exportCsv}
          disabled={data.count === 0}
          data-testid="partners-export-csv"
          className="overline px-5 py-3 border border-white/25 text-white hover:bg-white hover:text-black transition-colors disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>
      {data.count === 0 && <p className="text-white/40 mt-6">No partner inquiries yet.</p>}
      <div className="mt-6 space-y-4">
        {(data.partners || []).map((p) => (
          <div key={p.id} data-testid="partner-row" className="border border-white/12 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-serif text-white text-xl font-light">{p.business}</p>
                <p className="text-white/60 text-sm mt-1">{p.contact_name}</p>
              </div>
              <span className="text-white/40 text-xs">{(p.created_at || "").slice(0, 10)}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.commitment && <span className="overline text-[10px] px-3 py-1 border border-white/20 text-white/70">{p.commitment}</span>}
              {p.amount && <span className="overline text-[10px] px-3 py-1 border border-white/20 text-white/70">{p.amount}</span>}
              {p.contribution_type && <span className="overline text-[10px] px-3 py-1 border border-white/20 text-white/70">{p.contribution_type}</span>}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-white/75">
              <p><a href={`mailto:${p.email}`} className="hover:text-white underline">{p.email}</a></p>
              {p.phone && <p><a href={`tel:${p.phone}`} className="hover:text-white">{p.phone}</a></p>}
              {p.recognition_name && <p className="text-white/50">Recognition: {p.recognition_name}</p>}
            </div>
            {p.in_kind_description && <p className="text-white/60 text-sm mt-3">In-kind: {p.in_kind_description}</p>}
            {p.message && <p className="text-white/60 text-sm mt-3 italic">&ldquo;{p.message}&rdquo;</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubscribersTab({ authHeader }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/newsletter`, authHeader);
    setData(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-white/40">Loading…</p>;
  return (
    <div data-testid="subscribers-tab">
      <p className="overline text-white/50">Subscribers ({data.count})</p>
      <div className="mt-5 space-y-2">
        {(data.subscribers || []).map((s) => (
          <div key={s.id || s.email} className="flex items-center justify-between border border-white/12 px-4 py-3">
            <span className="text-white/85">{s.email}</span>
            <span className="text-white/40 text-xs">{(s.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
        {data.count === 0 && <p className="text-white/40">No subscribers yet.</p>}
      </div>
    </div>
  );
}
